import { useEffect, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { CheckCircle2, FileText, AlertTriangle } from 'lucide-react';
import {
  buildConfirmationDraftForProject,
  buildSnapshotForProject,
  createOrderConfirmation,
  fetchLatestConfirmation,
  setAkkoord,
  type OrderConfirmationRow,
} from '@/lib/api/confirmations';
import { snapshotsDiffer } from '@/lib/orderConfirmation';
import { OrderConfirmationPdf } from '@/pdf/OrderConfirmationPdf';
import type { Project } from '@/lib/types';

interface Props {
  project: Project;
  onChanged?: () => void;
}

export function OrderConfirmationPanel({ project, onChanged }: Props) {
  const [latest, setLatest] = useState<OrderConfirmationRow | null>(null);
  const [drift, setDrift] = useState(false);
  const [busy, setBusy] = useState<null | 'akkoord' | 'send'>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  async function refresh() {
    try {
      const row = await fetchLatestConfirmation(project.id);
      setLatest(row);
      if (row) {
        const current = await buildSnapshotForProject(project.id);
        setDrift(snapshotsDiffer(row.snapshot, current));
      } else {
        setDrift(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    }
  }

  async function handleAkkoord() {
    setBusy('akkoord');
    setError(null);
    try {
      await setAkkoord(project.id);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Akkoord vastleggen mislukt');
    } finally {
      setBusy(null);
    }
  }

  async function handleSend() {
    setBusy('send');
    setError(null);
    try {
      const { snapshot, pricingGuard } = await buildConfirmationDraftForProject(project.id);
      const row = await createOrderConfirmation(project.id, snapshot, pricingGuard);
      const blob = await pdf(
        <OrderConfirmationPdf snapshot={snapshot} version={row.version} date={row.sent_at} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orderbevestiging-${project.name}-v${row.version}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Versturen mislukt');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-xl border border-bmade-200 bg-white p-4 dark:border-bmade-800 dark:bg-bmade-900/40">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-bmade-950 dark:text-white">Orderbevestiging</h3>
        {project.akkoord_at ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5" /> Akkoord {new Date(project.akkoord_at).toLocaleDateString('nl-NL')}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => void handleAkkoord()}
            disabled={busy !== null}
            className="rounded-md border border-bmade-200 px-2 py-1 text-xs text-bmade-700 hover:bg-bmade-100 disabled:opacity-40 dark:border-bmade-700 dark:text-bmade-300 dark:hover:bg-bmade-800"
          >
            {busy === 'akkoord' ? 'Bezig...' : 'Akkoord vastleggen'}
          </button>
        )}
      </div>

      {error && <p className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

      {latest ? (
        <p className="text-xs text-bmade-600 dark:text-bmade-400">
          Laatste: versie {latest.version} · verstuurd{' '}
          {new Date(latest.sent_at).toLocaleDateString('nl-NL')}
        </p>
      ) : (
        <p className="text-xs text-bmade-500">Nog geen orderbevestiging verstuurd.</p>
      )}

      {drift && (
        <div className="mt-2 flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Order is gewijzigd na de laatste bevestiging. Klant heeft nog v{latest?.version}.
        </div>
      )}

      <button
        type="button"
        onClick={() => void handleSend()}
        disabled={busy !== null}
        className="mt-3 inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600 disabled:opacity-40"
      >
        <FileText className="h-4 w-4" />
        {busy === 'send'
          ? 'Genereren...'
          : latest
            ? 'Nieuwe versie versturen'
            : 'Orderbevestiging versturen'}
      </button>
    </div>
  );
}
